/**
 * Normalizes the `BILLING_COMPANY_AVAILABLE_DOCUMENTS` allowlist into comparable brand values.
 *
 * Accepts the JSON array the tenant config holds and the comma-separated string it may arrive as.
 * Anything else — including the boolean this feature used to be — reads as unconfigured, so a
 * tenant left on the old flag keeps the flow off instead of opening it to every document.
 *
 * @param {Array<string>|string} value - Raw feature value.
 * @returns {string[]} Trimmed, upper-cased document brand values.
 */
export function normalizeBillingCompanyDocuments(value) {
  if (typeof value !== 'string' && !Array.isArray(value)) return [];

  const entries = Array.isArray(value) ? value : value.split(',');

  return entries
    .map((entry) => {
      const normalized = String(entry ?? '').trim();
      return normalized.toUpperCase();
    })
    .filter(Boolean);
}

/**
 * Resolves the effective document allowlist behind the billing-company firewall.
 *
 * The boolean switch fails closed: a populated allowlist cannot activate any part of the company
 * flow unless the tenant explicitly enables it.
 *
 * @param {boolean} enabled - `BILLING_COMPANY_ENABLED` tenant configuration.
 * @param {Array<string>|string} value - Raw `BILLING_COMPANY_AVAILABLE_DOCUMENTS` value.
 * @returns {string[]} Normalized allowlist, or an empty list while the firewall is closed.
 */
export function getEnabledBillingCompanyDocuments(enabled, value) {
  if (enabled !== true) return [];

  return normalizeBillingCompanyDocuments(value);
}

/**
 * Whether a document type may resolve or create a billing company.
 *
 * A personal document leaves the flow: it never searches the provider and never ships the company
 * fields the backend would register a natural person with.
 *
 * @param {string} documentType - Purchaser document brand value.
 * @param {string[]} allowedDocuments - Allowlist from `normalizeBillingCompanyDocuments`.
 * @returns {boolean} Whether the billing-company flow applies to this document.
 */
export function isBillingCompanyDocument(documentType, allowedDocuments) {
  if (!documentType || !allowedDocuments?.length) return false;

  return allowedDocuments.includes(String(documentType).trim().toUpperCase());
}

/**
 * Whether company-specific purchaser behavior applies to a document type.
 *
 * A configured allowlist is authoritative. Without one, preserve the historical NIT behavior so
 * enabling unrelated purchaser features cannot change existing tenants.
 *
 * @param {string} documentType - Purchaser document brand value.
 * @param {string[]} allowedDocuments - Allowlist from `normalizeBillingCompanyDocuments`.
 * @returns {boolean} Whether company-specific purchaser fields and serialization apply.
 */
export function isBillingCompanyDocumentOrLegacy(documentType, allowedDocuments) {
  if (allowedDocuments?.length) {
    return isBillingCompanyDocument(documentType, allowedDocuments);
  }

  return (
    String(documentType || '')
      .trim()
      .toUpperCase() === 'NIT'
  );
}
